/**
 * ReactionDisplay Component
 * Shows grouped emoji reactions below message
 * Toggle behavior: Click to add/remove your reaction
 */

import { Reaction } from '@/types/socketEvents'

interface ReactionDisplayProps {
  reactions: Reaction[]
  currentUserId: string
  onToggleReaction: (emoji: string) => void
}

export function ReactionDisplay({ reactions, currentUserId, onToggleReaction }: ReactionDisplayProps) {
  if (!reactions || reactions.length === 0) {
    return null
  }

  // Group reactions by emoji
  const groupedReactions = reactions.reduce((acc, reaction) => {
    if (!acc[reaction.emoji]) {
      acc[reaction.emoji] = {
        emoji: reaction.emoji,
        count: 0,
        userIds: [],
        hasCurrentUser: false
      }
    }
    acc[reaction.emoji].count++
    acc[reaction.emoji].userIds.push(reaction.user_id)
    if (reaction.user_id === currentUserId) {
      acc[reaction.emoji].hasCurrentUser = true
    }
    return acc
  }, {} as Record<string, { emoji: string; count: number; userIds: string[]; hasCurrentUser: boolean }>)

  const reactionGroups = Object.values(groupedReactions)

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {reactionGroups.map(({ emoji, count, hasCurrentUser }) => (
        <button
          key={emoji}
          onClick={() => onToggleReaction(emoji)}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-all duration-150 shadow-md ${
            hasCurrentUser
              ? 'bg-blue-100 border-2 border-blue-500 text-blue-700 font-semibold hover:bg-blue-200'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400'
          }`}
          aria-label={`${hasCurrentUser ? 'Remove' : 'Add'} reaction ${emoji}`}
        >
          <span className="text-base leading-none">{emoji}</span>
          {count > 1 && <span className="text-xs font-medium">{count}</span>}
        </button>
      ))}
    </div>
  )
}
